import { toast } from 'react-toastify';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import header from '../../../assets/icm-header.jpg';
import {
  getBase64PDFImage,
  scaleStyles,
  baseStyles,
  getPrefix,
  formatarIdadeMeses,
} from '../../../util/utils';
import {
  getPdfBase64,
  enviarArquivo,
  normalizeWhatsappPhone,
} from '../../../services/pdfWhatsapp';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Trata o texto do laudo dividindo nos marcadores e aplicando a quebra de página desejada
 */
function processarTextoCongenito(textoObservacoes, quebraPagina) {
  if (!textoObservacoes) return [];

  // Mapeamento das tags para busca no texto
  let termoQuebra = '';
  if (quebraPagina === 'aorta') termoQuebra = 'AORTA:';
  else if (quebraPagina === 'coronarias') termoQuebra = 'ARTÉRIAS CORONÁRIAS:';
  else if (quebraPagina === 'conclusao') termoQuebra = 'CONCLUSÃO:';

  // Se for 'sem' ou o termo não existir no texto, insere o bloco normal
  if (
    quebraPagina === 'sem' ||
    !termoQuebra ||
    !textoObservacoes.includes(termoQuebra)
  ) {
    return [
      {
        text: textoObservacoes,
        style: 'observations',
        margin: [0, 0, 0, 10],
      },
    ];
  }

  const pos = textoObservacoes.indexOf(termoQuebra);
  const parte1 = textoObservacoes.substring(0, pos).trim();
  const parte2 = textoObservacoes.substring(pos).trim();

  return [
    {
      text: parte1,
      style: 'observations',
      margin: [0, 0, 0, 10],
    },
    {
      text: parte2,
      style: 'observations',
      pageBreak: 'before',
      margin: [0, 0, 0, 10],
    },
  ];
}

export default async function generateCongTXTPDF(
  cong,
  tipo = 'imprimirTimbrado',
  fontScale = 1.0,
  quebraPagina = 'sem',
  options = {}
) {
  const { cpfPaciente = false } = options || {};
  const styles = scaleStyles(baseStyles, fontScale);
  const headerImage =
    tipo === 'imprimirTimbrado' || tipo === 'envioWpp'
      ? await getBase64PDFImage(header)
      : null;

  // Processa o texto aplicando quebra de página quando necessário
  const textoProcessado = processarTextoCongenito(
    cong?.OBSERVACOES,
    quebraPagina
  );

  const assinaturaDigital = cong?.HASH || md5(JSON.stringify(cong || {}));

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: headerImage ? [40, 130, 40, 60] : [40, 60, 40, 60],
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: 'center',
          style: 'footer',
        },
      ],
      margin: [0, 10, 0, 0],
    }),
    content: [
      {
        text: 'ECOCARDIOGRAMA CONGÊNITO',
        style: 'title',
        alignment: 'center',
        margin: [0, 0, 0, 0],
      },
      // --- Cabeçalho com retângulo preto ---
      {
        table: {
          widths: ['*'], // ocupa toda a largura
          body: [
            [
              {
                stack: [
                  {
                    columns: [
                      {
                        text: `Nome do Paciente: ${cong?.CODPAC || ''} - ${
                          cong?.NOMEPAC || ''
                        }${
                          cpfPaciente && cong?.CPFPAC
                            ? ` - CPF: ${cong.CPFPAC}`
                            : ''
                        }`,
                        width: '100%',
                        bold: true,
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `Data Nasc: ${
                          cong?.dataNasc || cong?.dataNascFmt || ''
                        }`,
                        width: '50%',
                      },
                      {
                        text: `Sexo: ${cong?.Sexo || cong?.SEXOPAC || ''}`,
                        width: '20%',
                      },
                      {
                        text: `Idade: ${
                          cong?.DTNPAC
                            ? formatarIdadeMeses(cong?.DTNPAC, cong?.DATAECO)
                            : '--'
                        }`,
                        width: '30%',
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `Convênio: ${cong?.SIGLACONV || ''}`,
                        width: '50%',
                      },
                      {
                        text: `Código: ${cong?.CODECO || ''}`,
                        width: '20%',
                      },
                      {
                        text: `Data Laudo: ${cong?.dataEcoFmt || ''}`,
                        width: '30%',
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `Médico que Solicitou: ${getPrefix(
                          cong?.CODSOLICITANTE
                        )}${cong?.Solicitou || ''}`,
                        width: '100%',
                      },
                    ],
                  },
                ],
                margin: [10, 0, 10, 0],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => 'black',
          vLineColor: () => 'black',
        },
        style: 'content',
      },
      ...textoProcessado,
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `Imagens disponíveis no site: www.icm.com.br (Resultado de Exames)\nCódigo de verificação: ${
                  cong?.CODPAC || ''
                }`,
                style: 'files',
                alignment: 'center',
              },
            ],
          ],
        },
        layout: {
          hLineColor: '#999',
          vLineColor: '#999',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
      },
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  {
                    text: 'Conferido e assinado eletronicamente por:',
                    italics: true,
                    alignment: 'center',
                  },
                  {
                    text: `${getPrefix(cong?.CODREALIZOU)}${
                      cong?.Realizou || ''
                    }`,
                    bold: true,
                    alignment: 'center',
                  },
                  {
                    text: `CRM: ${cong?.CRMMED || ''} - RQE: ${
                      cong?.RQE || ''
                    }`,
                    bold: true,
                    style: 'small',
                    alignment: 'center',
                  },
                  {
                    text: `Assinatura Digital: ${assinaturaDigital}`,
                    italics: true,
                    style: 'smaller',
                    alignment: 'center',
                  },
                  {
                    text: [
                      { text: 'Emitido em: ', bold: true },
                      `${new Date().toLocaleString('pt-BR')}\n`,
                    ],
                    style: 'smaller',
                    color: '#444',
                    alignment: 'center',
                  },
                ],
                style: 'content',
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: '#999',
          vLineColor: '#999',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
        margin: [0, 5, 0, 0],
      },
    ],
    styles,
  };

  // Adiciona header apenas quando houver imagem
  if (headerImage) {
    docDefinition.header = {
      image: headerImage,
      width: 500,
      alignment: 'center',
      margin: [0, 10, 0, 0],
    };
  }

  if (tipo === 'imprimirNormal' || tipo === 'imprimirTimbrado') {
    pdfMake.createPdf(docDefinition).open();
  } else if (tipo === 'envioWpp') {
    const celular = normalizeWhatsappPhone(cong?.CELULARPAC);
    if (!celular) {
      toast.warn('Número de celular não encontrado!');
      return undefined;
    }

    try {
      const pdfGenerator = pdfMake.createPdf(docDefinition);
      const pdfBase64 = await getPdfBase64(pdfGenerator);
      const data = await enviarArquivo({
        to: celular,
        pdfBase64,
        paciente: {
          id: cong?.CODPAC,
          nome: cong?.NOMEPAC,
          medico: cong?.Realizou,
        },
        codLaudo: cong?.CODECO,
        fileName: `Laudo_Cong_${cong?.CODECO}.pdf`,
        caption: `Olá *${cong?.NOMEPAC}*, tudo bem? 😊

Segue em anexo o seu *Laudo de Ecocardiograma Congênito*.

As *imagens do exame* podem ser acessadas pelo link:
👉 http://www.icm.com.br (Resultados de Exames)

Qualquer dúvida, nossa equipe estará à disposição no setor de atendimento pelo WhatsApp 14-3402-5252\n
ICM - Instituto do Coração de Marília`,
        officialConfig: {
          procedimento: 'eco',
          documentTitle: 'Laudo de Ecocardiograma Congênito',
        },
      });
      toast.success(data.message);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        'Erro ao enviar laudo via WhatsApp';
      toast.error(errorMessage);
    }
  } else {
    pdfMake.createPdf(docDefinition).download(`Laudo-${cong?.CODPAC}.pdf`);
  }

  return docDefinition;
}
